import { SelectField } from "$components/rhf-fields";
import {
  Table,
  type TableColumnDefinition,
  type TableRowDefinition,
} from "$components/table";
import RHFZeroOrGreaterMonetaryAmountInPoundsField from "$routes/price-list/_lib/components/rhf/rhf-zero-or-greater-monetary-amount-in-pounds-field";
import useCurrencyFormat from "$routes/price-list/_lib/currencies";
import { Checkbox, CircularProgress, makeStyles } from "@material-ui/core";
import type { RevenueDepartment } from "elephant-web/gql";
import React from "react";
import { useFormContext, useWatch } from "react-hook-form";
import { useIntl } from "react-intl";
import type { AddItemsFixedRowData } from "./add-items-fixed-row-data";
import ItemNameWithSupplyChip from "./item-name-with-supply-chip";
import type { AddItemsFormData } from "./use-form-schema";

const _useStyles = makeStyles((theme) => ({
  selectFieldContainer: {
    display: "block",
    textAlign: "center",
  },
  error: {
    color: theme.palette.error.main,
    fontSize: "0.75rem",
  },
}));

const HideableTextField: FC<{
  item: TableRowDefinition;
}> = ({ item }) => {
  const { watch } = useFormContext();
  const rowFormData = watch(`${item.id}`);

  const name = `${item.id}.price`;
  return rowFormData ? (
    <RHFZeroOrGreaterMonetaryAmountInPoundsField
      name={name}
      inputProps={{ "aria-label": `${item.name} price` }}
      data-testid={name}
    />
  ) : null;
};

const HideableSelectField: FC<
  { rowId: string | number } & React.ComponentProps<typeof SelectField>
> = ({ rowId, ...delegateProps }) => {
  const rowData = useWatch({ name: `${rowId}` });

  return rowData ? <SelectField {...delegateProps} /> : null;
};

const FormDefaultInjectingCheckbox: FC<{
  dataToInject: AddItemsFormData[0];
  rowId: string | number;
}> = ({ dataToInject, rowId }) => {
  const { getValues, setValue } = useFormContext();
  const onChecked = () => {
    setValue(`${rowId}`, { ...dataToInject });
  };
  const onUnchecked = () => {
    setValue(`${rowId}`, null);
  };
  return (
    <Checkbox
      inputProps={{ "aria-labelledby": `item-name-${rowId}` }}
      checked={!!getValues()[rowId]}
      onChange={(_, checked) => (checked ? onChecked() : onUnchecked())}
    />
  );
};

type Props = {
  fixedRowData: AddItemsFixedRowData[];
  revenueDepartments: readonly RevenueDepartment[];
} & Omit<React.ComponentProps<typeof Table>, "columns" | "rows">;

const AddItemsTable: FC<Props> = ({
  fixedRowData,
  revenueDepartments,
  ...delegateProps
}) => {
  const { formatMessage } = useIntl();
  const currency = useCurrencyFormat();
  const formattedCurrency = currency?.formatToParts(Number(currency))[0].value;

  const revenueDepartmentsOptions = revenueDepartments.map((r) => ({
    value: r.id,
    label: r.name,
  }));

  const columns: TableColumnDefinition[] = [
    {
      field: "id",
      flex: 1,
      renderCell: (_, __, ___, row) => {
        if (revenueDepartments.length === 0) {
          return <CircularProgress />;
        }
        return (
          <FormDefaultInjectingCheckbox
            rowId={row.id}
            dataToInject={{
              name: row.name,
              tariffId: row.tariff.id,
              externalReference: {
                foreignId: `${row.id}`,
                origin: row.origin,
              },
              price: 0,
              revenueDepartmentId: revenueDepartments[0].id,
            }}
          />
        );
      },
    },
    {
      field: "name",
      flex: 4,
      headerName: formatMessage({
        id: "priceList.addItems.column.name",
        defaultMessage: "Item",
      }),
      renderCell: (value, _, __, row) => {
        return <ItemNameWithSupplyChip copy={value} data={row} />;
      },
    },
    {
      field: "origin",
      flex: 2,
      headerName: formatMessage({
        id: "priceList.addItems.column.origin",
        defaultMessage: "Type",
      }),
      renderCell: (value) => (
        <p style={{ textTransform: "capitalize" }}>{value.toLowerCase()}</p>
      ),
    },
    {
      field: "price",
      flex: 2,
      headerName: `${formatMessage({
        id: "priceList.addItems.column.price",
        defaultMessage: "Price",
      })} (${formattedCurrency})`,
      renderCell: (_, __, ___, row) => <HideableTextField item={row} />,
    },
    {
      field: "tariff",
      flex: 2,
      headerName: formatMessage({
        id: "priceList.addItems.column.tariff",
        defaultMessage: "Price Type",
      }),
      renderCell: (data) => data.name,
    },
    {
      field: "revenueDepartmentId",
      flex: 4,
      headerName: formatMessage({
        id: "priceList.addItems.column.revenueDepartment",
        defaultMessage: "Revenue Department",
      }),
      renderCell: (_, __, ___, row) => {
        if (revenueDepartments.length === 0) {
          return <CircularProgress />;
        }
        return (
          <HideableSelectField
            name={`${row.id}.revenueDepartmentId`}
            rowId={row.id}
            hideHelper
            options={revenueDepartmentsOptions}
            selectProps={{ "aria-label": `${row.name} revenue department` }}
          />
        );
      },
    },
  ];

  return <Table columns={columns} rows={fixedRowData} {...delegateProps} />;
};

export default AddItemsTable;
